// Sprint dispatch-lineage-ui (D4b) — a dispatched worker, rendered INSIDE
// its caller's conversation with the SAME component as an in-process
// subagent. Santi, 2026-09-09: he wants to see dispatched workers
// "EXACTAMENTE como ve a un subagente in-process — una tarjeta plegable
// DENTRO de la conversación del caller — y NADA de ellos en su barra
// lateral".
//
// This file is an ADAPTER, not a panel: it turns one `dispatch_progress`
// row (the ledger snapshot the control plane upserts, spec D4) into the
// props `SubagentPanel` already takes —
//   toolUseMessage   — a synthetic `Agent` launch: subagent_type = branch,
//                      prompt = the dispatched task;
//   childrenByParent — the worker's own transcript, fetched lazily on the
//                      first expand (`onOpenChange`), grouped exactly as
//                      the conversation view groups it so the worker's
//                      nested subagents still render as nested panels;
//   resultMessage    — synthetic, present ONLY once the ledger is terminal
//                      (so the panel's own `isRunning = !tr` and the rose
//                      error border keep their meaning): body = the
//                      worker's last real assistant text when loaded.
// The live "working / quiet" half of the status is NOT in the row (D4):
// it is read from the worker's own `agent:updated` stream via the store.
//
// New (verified no equivalent): `WorkflowPanel` is a sibling for the CLI's
// in-process Workflow tool; nothing renders a cross-session worker.

import { useCallback, useMemo, useState } from 'react';
import type { Agent, DispatchProgressContent, Message } from '../types';
import { SubagentPanel } from './SubagentPanel';
import { useAppStore } from '../stores/app.store';
import { fetchAgentMessages } from '../lib/api';
import { groupBySubagent } from '../lib/messages';
import { useTranslation } from '../i18n';
import type { TKey } from '../i18n';

// Terminals under which the worker demonstrably failed (rose header).
// `completed` + `result_failed` joins them (the worker said so itself).
const FAILED_TERMINALS = new Set(['agent_error', 'agent_stopped', 'spawn_failed', 'agent_gone']);

export interface DispatchCardStatus {
  key: TKey;
  isError: boolean;
  running: boolean;
  /** Gate 2 fix round (reviewer, 2026-09-13, blocker 1) — `true` ONLY for the terminal states
   * that actually mean "finished": `completed` (reported/reporting), non-failed. Every other
   * terminal (including `needs_founder`, which is settled + non-error but NOT done — the worker
   * stopped to ASK, not finish) and the open/running state are `false`. Feeds
   * `SubagentPanel`'s `settledIsDone` prop so its Check glyph never renders beside "(needs your
   * approval)". */
  done: boolean;
}

// Ledger terminal (+ the worker's live founder state while open) → copy.
// Exported for the test: the mapping is the founder-visible contract.
export function dispatchCardStatus(
  content: DispatchProgressContent,
  worker: Pick<NonNullable<Agent['founderStatus']>, 'state' | 'stalled'> | undefined,
): DispatchCardStatus {
  const s = content.terminalState;
  if (s === null) {
    const quiet = worker !== undefined && (worker.state === 'unconfirmed' || worker.stalled);
    return {
      key: quiet ? 'dispatchCard.quiet' : 'dispatchCard.working',
      isError: false,
      running: true,
      done: false,
    };
  }
  if (s === 'completed') {
    if (content.terminalDetail === 'result_failed') {
      return { key: 'dispatchCard.failed', isError: true, running: false, done: false };
    }
    return {
      key: content.reportedAt ? 'dispatchCard.reported' : 'dispatchCard.reporting',
      isError: false,
      running: false,
      done: true,
    };
  }
  if (FAILED_TERMINALS.has(s)) {
    return { key: 'dispatchCard.failed', isError: true, running: false, done: false };
  }
  if (s === 'deadline') return { key: 'dispatchCard.deadline', isError: true, running: false, done: false };
  if (s === 'needs_founder') {
    return { key: 'dispatchCard.needsFounder', isError: false, running: false, done: false };
  }
  if (s === 'spawn_timeout') {
    return { key: 'dispatchCard.noStart', isError: true, running: false, done: false };
  }
  return { key: 'dispatchCard.reported', isError: false, running: false, done: true };
}

export function asDispatchProgress(m: Message): DispatchProgressContent | null {
  const c = m.content as Partial<DispatchProgressContent> | null;
  if (!c || typeof c !== 'object' || typeof c.dispatchId !== 'string') return null;
  return {
    dispatchId: c.dispatchId,
    callerAgentId: typeof c.callerAgentId === 'string' ? c.callerAgentId : '',
    spawnedAgentId: typeof c.spawnedAgentId === 'string' ? c.spawnedAgentId : null,
    projectSlug: typeof c.projectSlug === 'string' ? c.projectSlug : '',
    branch: typeof c.branch === 'string' ? c.branch : '',
    task: typeof c.task === 'string' ? c.task : '',
    createdAt: typeof c.createdAt === 'string' ? c.createdAt : m.timestamp,
    deadlineAt: typeof c.deadlineAt === 'string' ? c.deadlineAt : '',
    terminalState: typeof c.terminalState === 'string' ? c.terminalState : null,
    terminalDetail: typeof c.terminalDetail === 'string' ? c.terminalDetail : null,
    terminalAt: typeof c.terminalAt === 'string' ? c.terminalAt : null,
    reportedAt: typeof c.reportedAt === 'string' ? c.reportedAt : null,
    updatedAt: typeof c.updatedAt === 'string' ? c.updatedAt : m.timestamp,
  };
}

// The worker's last REAL assistant text (the `<synthetic>` model tag is
// the CLI's own "Prompt is too long", never the worker's result — the
// same fence the watch pass applies server-side).
function lastRealAssistantText(messages: readonly Message[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]!;
    if (m.type !== 'assistant_text') continue;
    const c = m.content as { text?: unknown; model?: unknown } | null;
    if (c && c.model === '<synthetic>') continue;
    if (c && typeof c.text === 'string') return c.text;
  }
  return null;
}

export function DispatchWorkerCard({ message }: { message: Message }) {
  const { t } = useTranslation();
  const content = useMemo(() => asDispatchProgress(message), [message]);
  const workerId = content?.spawnedAgentId ?? null;
  const worker = useAppStore((s) => (workerId ? s.agents[workerId] : undefined));

  const [transcript, setTranscript] = useState<Message[] | null>(null);
  const [requested, setRequested] = useState(false);
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open || requested || !workerId) return;
      setRequested(true);
      fetchAgentMessages(workerId, { limit: 200 })
        .then((r) => setTranscript(r.messages))
        .catch(() => setTranscript([]));
    },
    [requested, workerId],
  );

  if (!content) return null;

  const status = dispatchCardStatus(content, worker?.founderStatus);
  const statusLabel = t(status.key);
  const toolUseId = `dispatch:${content.dispatchId}`;

  const toolUseMessage: Message = {
    id: `${message.id}:launch`,
    agentId: message.agentId,
    type: 'tool_use',
    timestamp: content.createdAt,
    content: {
      tool_use_id: toolUseId,
      name: 'Agent',
      input: { subagent_type: content.branch, prompt: content.task },
    },
  };

  const childrenByParent = (() => {
    const map = new Map<string, Message[]>();
    if (transcript === null) {
      // cursor-ux-mirror §2.3 — visible loading on every action >200 ms:
      // while the transcript fetch is in flight the panel shows one
      // synthetic child (never a real assistant text: `<synthetic>` keeps
      // it out of `lastRealAssistantText`).
      map.set(
        toolUseId,
        requested
          ? [
              {
                id: `${message.id}:loading`,
                agentId: message.agentId,
                type: 'assistant_text',
                timestamp: content.updatedAt,
                content: { text: t('dispatchCard.loading'), model: '<synthetic>' },
              } as Message,
            ]
          : [],
      );
      return map;
    }
    const grouped = groupBySubagent(transcript);
    for (const [k, v] of grouped.childrenByParent) map.set(k, v);
    map.set(toolUseId, grouped.topLevel);
    return map;
  })();

  const resultMessage: Message | undefined = content.terminalState
    ? {
        id: `${message.id}:result`,
        agentId: message.agentId,
        type: 'tool_result',
        timestamp: content.terminalAt ?? content.updatedAt,
        content: {
          tool_use_id: toolUseId,
          content: (transcript && lastRealAssistantText(transcript)) ?? statusLabel,
          is_error: status.isError,
        },
      }
    : undefined;

  return (
    <div
      data-testid="dispatch-worker-card"
      data-dispatch-id={content.dispatchId}
      data-terminal-state={content.terminalState ?? 'open'}
      data-transcript={transcript === null ? (requested ? 'loading' : 'idle') : 'loaded'}
    >
      <SubagentPanel
        toolUseMessage={toolUseMessage}
        childrenByParent={childrenByParent}
        resultMessage={resultMessage}
        label={t('dispatchCard.label')}
        statusLabel={statusLabel}
        onOpenChange={handleOpenChange}
        settledIsDone={status.done}
      />
    </div>
  );
}
